import { Injectable, inject, DestroyRef } from '@angular/core';
import {
  Router,
  NavigationEnd,
  ActivatedRouteSnapshot,
  Data
} from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface Breadcrumb {
  label: string;
  url: string;
}

export interface HeaderData {
  title: string;
  breadcrumbs: Breadcrumb[];
}

@Injectable({
  providedIn: 'root'
})
export class BreadcrumbService {
  private readonly _breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);
  private readonly _headerData$ = new BehaviorSubject<HeaderData>({
    title: '',
    breadcrumbs: []
  });
  private routeParamsMap: Map<string, { [key: string]: unknown }> = new Map();

  // Observable exposing the breadcrumb hierarchy
  readonly breadcrumbs$ = this._breadcrumbs$.asObservable();
  readonly headerData$ = this._headerData$.asObservable();

  private readonly destroyRef = inject(DestroyRef);

  constructor(private router: Router) {
    this.router.events
      .pipe(
        // Filter the NavigationEnd events as the breadcrumb is updated only when the route reaches its end
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: NavigationEnd) => {
        // Capture and store current route query params
        const urlTree = this.router.parseUrl(event.url);
        const currentPath = this.getPathWithoutParams(event.url);

        // Store the query params for this specific route path
        if (Object.keys(urlTree.queryParams).length > 0) {
          this.routeParamsMap.set(currentPath, urlTree.queryParams);
        }

        // Construct the breadcrumb hierarchy
        const root = this.router.routerState.snapshot.root;
        const breadcrumbs: Breadcrumb[] = [];
        const data: HeaderData = { title: '', breadcrumbs: [] };
        this.addBreadcrumb(root, [], breadcrumbs, data);

        // Clean up params for routes that are no longer in breadcrumbs
        this.cleanupRouteParams(breadcrumbs);

        // Emit the new hierarchy
        this._breadcrumbs$.next(breadcrumbs);
        this._headerData$.next(data);
      });
  }

  private cleanupRouteParams(breadcrumbs: Breadcrumb[]): void {
    const activePaths = new Set(
      breadcrumbs.map((b) => this.getPathWithoutParams(b.url))
    );

    // Remove params for routes that are not in current breadcrumbs
    for (const path of this.routeParamsMap.keys()) {
      if (!activePaths.has(path)) {
        this.routeParamsMap.delete(path);
      }
    }
  }

  updateBreadCumLabel(oLabel: string, newLabel: string) {
    this.headerData$
      .pipe()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((val) => {
        if (val && val.breadcrumbs && val.breadcrumbs.length > 0) {
          val.breadcrumbs.forEach((b) => {
            if (b.label === oLabel) {
              b.label = newLabel;
            }
          });
        }
      });
  }

  private addBreadcrumb(
    route: ActivatedRouteSnapshot | null,
    parentUrl: string[],
    breadcrumbs: Breadcrumb[],
    data: HeaderData
  ) {
    if (route) {
      // Construct the route URL
      const routeUrl = parentUrl.concat(route.url.map((url) => url.path));

      if (route.data['title']) {
        data.title = String(route.data['title']);
      }

      // Add an element for the current route part
      if (route.data['breadcrumb']) {
        // Build base URL
        const pathSegment = '/' + routeUrl.join('/');
        let url = pathSegment;

        // Try to get stored query params for this specific route path
        const queryParams = this.routeParamsMap.get(pathSegment) || {};

        // Add query parameters if present
        if (Object.keys(queryParams).length > 0) {
          const queryString = Object.entries(queryParams)
            .map(
              ([key, value]) =>
                `${encodeURIComponent(key)}=${encodeURIComponent(
                  String(value)
                )}`
            )
            .join('&');
          url += `?${queryString}`;
        }

        const breadcrumb = {
          label: String(this.getLabel(route.data)),
          url
        };
        data.breadcrumbs.push(breadcrumb);
        breadcrumbs.push(breadcrumb);
      }

      // Add another element for the next route part
      this.addBreadcrumb(route.firstChild, routeUrl, breadcrumbs, data);
    }
  }

  // Helper method to extract path without query params
  private getPathWithoutParams(url: string): string {
    const questionMarkIndex = url.indexOf('?');
    return questionMarkIndex >= 0 ? url.substring(0, questionMarkIndex) : url;
  }

  private getLabel(data: Data) {
    // The breadcrumb can be defined as a static string or as a function to construct the breadcrumb element out of the route data
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return typeof data['breadcrumb'] === 'function'
      ? // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        data['breadcrumb'](data)
      : data['breadcrumb'];
  }
}
